import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';

export type CabinetPrefillSettingsSectionProps = {
  cabinetId: number;
  restBaseUrl: string;
  nonce: string;
  onSessionExpired: () => void;
};

type PrefillSettingsPayload = {
  enabled: boolean;
  canEdit: boolean;
};

type SectionState = 'loading' | 'ready' | 'saving' | 'unavailable' | 'expired';

export type PrefillApplicationSettings = { enabled: boolean; source: 'row' | 'default' };

const TOGGLE_LABEL = __('Préremplissage automatique par les registres publics');
const APPLICATION_TOGGLE_LABEL = __('Pré-remplir le formulaire VSME');

/** Carries the HTTP status so the switch can tell a session expiry (401) from a refusal (403). */
class SettingsRequestError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const isPrefillApplicationSettings = (value: unknown): value is PrefillApplicationSettings => {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return typeof o.enabled === 'boolean' && (o.source === 'row' || o.source === 'default');
};

const requestApplicationSettings = async (
  endpoint: string,
  nonce: string,
  init: { method: 'GET' } | { method: 'POST'; enabled: boolean },
  malformedMessage: string,
): Promise<PrefillApplicationSettings> => {
  const response = await fetchWithNonce(endpoint, {
    method: init.method,
    credentials: 'same-origin',
    headers:
      init.method === 'POST'
        ? { 'X-WP-Nonce': nonce, 'Content-Type': 'application/json' }
        : { 'X-WP-Nonce': nonce },
    ...(init.method === 'POST' ? { body: JSON.stringify({ enabled: init.enabled }) } : {}),
  });

  if (!response.ok) {
    throw new SettingsRequestError(response.status, await parseApiError(response));
  }

  const data: unknown = await response.json();
  if (!isPrefillApplicationSettings(data)) {
    throw new SettingsRequestError(response.status, malformedMessage);
  }
  return data;
};

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

type PrefillApplicationSwitchProps = {
  cabinetId: number;
  restBaseUrl: string;
  nonce: string;
  onSessionExpired: () => void;
};

/**
 * Whether stored data points fill the VSME form. Independent from the acquisition switch above:
 * its own query, its own mutation, its own error. Like the acquisition switch, the ticked state is
 * the server's answer, never the click — and a read failure shows no switch at all rather than a
 * default that might be false.
 */
const PrefillApplicationSwitch = ({
  cabinetId,
  restBaseUrl,
  nonce,
  onSessionExpired,
}: PrefillApplicationSwitchProps) => {
  const queryClient = useQueryClient();
  const endpoint = `${restBaseUrl}cabinets/${cabinetId}/prefill-application-settings`;
  const queryKey = ['cabinet-prefill-application-settings', cabinetId] as const;

  const query = useQuery<PrefillApplicationSettings, Error>({
    queryKey,
    queryFn: () => requestApplicationSettings(
        endpoint,
        nonce,
        { method: 'GET' },
        __('Le réglage n’a pas pu être chargé.'),
      ),
    retry: false,
  });

  const mutation = useMutation<PrefillApplicationSettings, Error, boolean>({
    mutationFn: (enabled: boolean) =>
      requestApplicationSettings(
        endpoint,
        nonce,
        { method: 'POST', enabled },
        __('Le réglage n’a pas pu être enregistré.'),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });

  const expired =
    (query.error instanceof SettingsRequestError && query.error.status === 401)
    || (mutation.error instanceof SettingsRequestError && mutation.error.status === 401);

  useEffect(() => {
    if (expired) onSessionExpired();
  }, [expired, onSessionExpired]);

  if (query.isPending) return null;

  if (query.isError) {
    if (expired) return null;
    return (
      <p className="notice notice-error" role="alert">
        {errorMessage(query.error, __('Le réglage n’a pas pu être chargé.'))}
      </p>
    );
  }

  const mutationError =
    mutation.isError && !expired
      ? errorMessage(mutation.error, __('Le réglage n’a pas pu être enregistré.'))
      : '';

  return (
    <>
      <div className="checkbox-container">
        <input
          type="checkbox"
          id="cabinet_prefill_application_enabled"
          checked={query.data.enabled}
          disabled={mutation.isPending}
          aria-describedby="cabinet_prefill_application_help"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            mutation.mutate(event.target.checked);
          }}
        />
        <label htmlFor="cabinet_prefill_application_enabled">{APPLICATION_TOGGLE_LABEL}</label>
      </div>
      <p className="description" id="cabinet_prefill_application_help">
        {__("Les données continuent d'être collectées ; le formulaire démarre vide.")}
      </p>
      {mutationError ? (
        <p className="notice notice-error" role="alert">
          {mutationError}
        </p>
      ) : null}
    </>
  );
};

/**
 * The cabinet owner's switch for automatic public-source prefill.
 *
 * Only ever rendered for the owner — `CabinetComplianceTab` gates on `bootstrap.isCabinetOwner`,
 * and the route refuses anyone else with a 403, so the two agree. The displayed state always comes
 * from the server's response rather than from the click, because a switch that shows "off" while
 * the cabinet is still fetching ten registers per dossier is worse than one that fails visibly.
 */
const CabinetPrefillSettingsSection = ({
  cabinetId,
  restBaseUrl,
  nonce,
  onSessionExpired,
}: CabinetPrefillSettingsSectionProps) => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [state, setState] = useState<SectionState>('loading');
  const [error, setError] = useState<string>('');

  const endpoint = `${restBaseUrl}cabinets/${cabinetId}/prefill-settings`;

  useEffect(() => {
    let cancelled = false;

    fetchWithNonce(endpoint, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-WP-Nonce': nonce },
    })
      .then(async (response) => {
        if (cancelled) return;
        if (response.status === 401) {
          onSessionExpired();
          setState('expired');
          return;
        }
        if (!response.ok) {
          setState('unavailable');
          setError(await parseApiError(response));
          return;
        }
        const data = (await response.json()) as PrefillSettingsPayload;
        setEnabled(data.enabled);
        setState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setState('unavailable');
        setError(__('Le réglage n’a pas pu être chargé.'));
      });

    return () => {
      cancelled = true;
    };
  }, [endpoint, nonce, onSessionExpired]);

  const handleToggle = useCallback(
    async (next: boolean) => {
      setState('saving');
      setError('');

      try {
        const response = await fetchWithNonce(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-WP-Nonce': nonce, 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: next }),
        });

        if (response.status === 401) {
          onSessionExpired();
          setState('expired');
          return;
        }

        if (!response.ok) {
          // The previous value stays on screen: nothing was stored, so showing the new one
          // would claim a change the cabinet does not have.
          setError(await parseApiError(response));
          setState('ready');
          return;
        }

        const data = (await response.json()) as PrefillSettingsPayload;
        setEnabled(data.enabled);
        setState('ready');
      } catch {
        setError(__('Le réglage n’a pas pu être enregistré.'));
        setState('ready');
      }
    },
    [endpoint, nonce, onSessionExpired],
  );

  if (state === 'expired') {
    // The session is over: nothing below could load, and the parent is already handling it.
    return null;
  }

  return (
    <section className="cabinet-prefill-settings">
      <h3>{__('Sources publiques')}</h3>
      <p>
        {__(
          'À la création d’un dossier, les dix registres publics sont consultés automatiquement pour préremplir la déclaration.',
        )}
      </p>
      {state === 'unavailable' ? null : (
        <>
      <div className="checkbox-container">
        <input
          type="checkbox"
          id="cabinet_prefill_enabled"
          checked={enabled}
          disabled={state !== 'ready'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            void handleToggle(event.target.checked);
          }}
        />
        <label htmlFor="cabinet_prefill_enabled">{TOGGLE_LABEL}</label>
      </div>
      <p className="description">
        {__(
          'Désactiver n’efface rien : les registres déjà consultés restent sur le dossier et une consultation en cours va à son terme. Seuls les dossiers créés ensuite ne consultent plus les registres.',
        )}
      </p>
        </>
      )}
      {error ? <p className="notice notice-error">{error}</p> : null}
      <PrefillApplicationSwitch
        cabinetId={cabinetId}
        restBaseUrl={restBaseUrl}
        nonce={nonce}
        onSessionExpired={onSessionExpired}
      />
    </section>
  );
};

export default CabinetPrefillSettingsSection;
